import style from "../../../assets/scss/sub/purchase/orderAndPayment.module.scss";
import {MainWrapper} from "../MainWrapper";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ArticleTitle} from "../ArticleTitle";
import {PaymentWay} from "../../../types/payment";
import {useAppDispatch, useAppSelector, useAuth, useLoader, useModal, useUserAgent} from "../../../hooks/hooks";
import {orderStatus} from "../../../store/order/orderSlice";
import {cartedProducts, getCartsAsync, setCartedProducts} from "../../../store/cart/cartSlice";
import {CartedProduct, Price} from "../../../types/cart";
import {PaymentGateway} from "./pg/PaymentGateway";
import {CodeDiscountPanel} from "./CodeDiscountPanel";
import {AffiliateCodeVO, DiscountType} from "../../../types/affiliateCode";
import {CouponCodeDTO} from "../../../types/couponCode";
import {AgreementField} from "./hooks/AgreementField";
import {OrderSummaryField} from "./hooks/OrderSummaryField";
import {PaymentMethodField} from "./hooks/PaymentMethodField";
import {CouponDiscountField} from "./hooks/CouponDiscountField";
import {OrderProductsField} from "./hooks/OrderProductsField";
import {useOrderFunctions} from "./hooks/useOrderFunctions";
import {useVoucherTypeGetter} from "./hooks/useVoucherTypeGetter";
import {UnitVoucherType} from "../../../types/common";

export function OrderAndPayment() {
  const dispatch = useAppDispatch();
  const cartedProductList = useAppSelector(cartedProducts);
  const status = useAppSelector(orderStatus);

  const { openAlertModal } = useModal();
  const { setLoaderStatus } = useLoader();
  const { isLoggedIn } = useAuth();
  const { isMobile } = useUserAgent();

  const [isAgreed, setIsAgreed] = useState(false);
  const [couponDiscountAmount, setCouponDiscountAmount] = useState(0);
  const [selectedCouponIdx, setSelectedCouponIdx] = useState<number | null>(null);
  const [paymentWay, setPaymentWay] = useState<PaymentWay | null>(null);
  const [isClickedPayButton, setIsClickedPayButton] = useState(false);

  const [affiliateCode, setAffiliateCode] = useState<AffiliateCodeVO | null>(null);
  const [couponCode, setCouponCode] = useState<CouponCodeDTO | null>(null);
  const [enteredCode, setEnteredCode] = useState("");
  const [affiliateCodeDiscountAmount, setAffiliateCodeDiscountAmount] = useState(0);
  const [couponCodeDiscountAmount, setCouponCodeDiscountAmount] = useState(0);

  const { getCartIdxesFromQueryParam, onClickTermsLink} = useOrderFunctions();
  const {getVoucherType, currentVoucherType} = useVoucherTypeGetter();

  const onClickAgreeBtn = useCallback((val: boolean) => setIsAgreed(!val), []);
  const onClickPaymentWayBtn = useCallback((p: PaymentWay) => setPaymentWay(p), []);
  const cartIdxes = getCartIdxesFromQueryParam();

  useEffect(() => {
    if (cartedProductList) {
      getVoucherType(cartedProductList[0].product.idx).then();
    }
  }, [cartedProductList,getVoucherType]);

  const onClickPayBtn = useCallback(() => {
    if (!isAgreed) {
      openAlertModal({
        title: "안내",
        desc: "주문 확인 후 약관동의 항목에 체크해주세요.",
      });
      return;
    }

    if (!paymentWay) {
      openAlertModal({
        title: "안내",
        desc: "결제수단을 선택해 주세요.",
      });

      return;
    }

    setIsClickedPayButton(true);
  }, [isAgreed, paymentWay, openAlertModal]);

  const totalPrice: Price = useMemo(() => {
    if (cartedProductList) {
      return cartedProductList.reduce(
          (acc: Price, curPrd) => {
            const thisRegularAmount = acc.regularAmount + (curPrd?.totalRegularPrice ?? 0);
            const thisProductDiscountAmount = acc.productDiscountAmount + (curPrd?.totalDiscountAmount ?? 0);
            const thisProductAmount = thisRegularAmount - thisProductDiscountAmount;
            const actualAmount =
                acc.actualAmount + (curPrd?.totalActualAmount ?? 0) - couponDiscountAmount - affiliateCodeDiscountAmount - couponCodeDiscountAmount;

            return {
              regularAmount: thisRegularAmount,
              productDiscountAmount: thisProductDiscountAmount,
              productDiscountAmountShown: thisProductDiscountAmount,
              couponDiscountAmount: couponDiscountAmount,
              couponDiscountAmountShown:
                  thisProductAmount < couponDiscountAmount ? thisProductAmount : couponDiscountAmount,
              affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
              affiliateCodeDiscountAmountShown:
                  thisProductAmount < affiliateCodeDiscountAmount ? thisProductAmount : affiliateCodeDiscountAmount,
              actualAmount: actualAmount,
              actualAmountShown: actualAmount > 0 ? actualAmount : 0,
              couponCodeDiscountAmount: couponCodeDiscountAmount,
              couponCodeDiscountAmountShown: thisProductAmount < couponCodeDiscountAmount ? thisProductAmount : couponCodeDiscountAmount,
            };
          },
          {
            regularAmount: 0,
            couponDiscountAmount: couponDiscountAmount,
            couponDiscountAmountShown: couponDiscountAmount,
            affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
            affiliateCodeDiscountAmountShown: affiliateCodeDiscountAmount,
            productDiscountAmount: 0,
            productDiscountAmountShown: 0,
            actualAmount: 0,
            actualAmountShown: 0,
            couponCodeDiscountAmount: couponCodeDiscountAmount,
            couponCodeDiscountAmountShown: couponCodeDiscountAmount,
          }
      );
    } else {
      return {
        regularAmount: 0,
        productDiscountAmount: 0,
        productDiscountAmountShown: 0,
        couponDiscountAmount: couponDiscountAmount,
        couponDiscountAmountShown: couponDiscountAmount,
        affiliateCodeDiscountAmount: affiliateCodeDiscountAmount,
        affiliateCodeDiscountAmountShown: affiliateCodeDiscountAmount,
        actualAmount: 0,
        actualAmountShown: 0,
        couponCodeDiscountAmount: couponCodeDiscountAmount,
        couponCodeDiscountAmountShown: couponCodeDiscountAmount,
      };
    }
  }, [cartedProductList, couponDiscountAmount, affiliateCodeDiscountAmount, couponCodeDiscountAmount]);
  const [codeDiscountAmount, setCodeDiscountAmount] =
      useState(Math.max(totalPrice.affiliateCodeDiscountAmount, totalPrice.couponCodeDiscountAmount));

  const getCarts = useCallback(async () => {
    try {
      const cartedProducts: CartedProduct[] = await dispatch(getCartsAsync(cartIdxes)).unwrap();
      dispatch(setCartedProducts(cartedProducts));
    } catch (error) {
      console.error(error);
      openAlertModal(null);
    }
  }, [dispatch, openAlertModal, cartIdxes]);

  useEffect(() => {
    document.title = `검사이용권 구매 | ${process.env.REACT_APP_TITLE}`;
    window.scroll({ top: 0 });

    if (isLoggedIn) {
      getCarts().then();
    }
    // eslint-disable-next-line
  }, [isLoggedIn]);

  useEffect(() => {
    setLoaderStatus([status]);
    // eslint-disable-next-line
  }, [status]);

  const resetCoupon = useCallback(() => {
    setCouponDiscountAmount(0);
    setSelectedCouponIdx(null);
    setCodeDiscountAmount(0);
  }, []);

  const resetSalesCodeDiscount = useCallback(() => {
    setAffiliateCode(null);
    setCouponCode(null)
    setCodeDiscountAmount(0)
    setEnteredCode("");
  }, []);

  const totalSellingPrice = useMemo(
      () =>
          cartedProductList?.map((it) => it.product.sellingPrice * it.quantity).reduce((total, it) => total + it, 0) ?? 0,
      [cartedProductList]
  );

  // 코드 할인 시 제휴 / 쿠폰코드 공통 초기화 & 값 할당 함수
  const resetAndSetCode = useCallback(
      (params: { type: 'affiliate', code: AffiliateCodeVO } | { type: 'coupon', code: CouponCodeDTO } | null) => {
        if (params) {
          resetCoupon();
          setAffiliateCode(params.type === 'affiliate' ? params.code : null);
          setCouponCode(params.type === 'coupon' ? params.code : null);
        } else {
          setAffiliateCode(null)
          setCouponCode(null)
        }
      },
      // eslint-disable-next-line
      [setAffiliateCode, setCouponCode]
  );

  useEffect(() => {
    if (affiliateCode) {
      switch (affiliateCode.discountType) {
        case DiscountType.AMOUNT:
          setAffiliateCodeDiscountAmount(affiliateCode.discountValue);
          break;
        case DiscountType.PERCENT:
          setAffiliateCodeDiscountAmount(Math.round(totalSellingPrice * (affiliateCode.discountValue / 100)));
          break;
        default:
          setAffiliateCodeDiscountAmount(0);
          break;
      }
    } else {
      setAffiliateCodeDiscountAmount(0);
    }
  }, [affiliateCode, totalSellingPrice]);

  useEffect(() => {
    if (couponCode) {
      switch (couponCode.discountType) {
        case DiscountType.AMOUNT:
          setCouponCodeDiscountAmount(couponCode.discountAmount);
          break;
        case DiscountType.PERCENT:
          setCouponCodeDiscountAmount(Math.round(totalSellingPrice * (couponCode.discountAmount / 100)));
          break;
        default:
          setCouponCodeDiscountAmount(0);
          break;
      }
    } else {
      setCouponCodeDiscountAmount(0);
    }
  }, [couponCode, totalSellingPrice]);

  const totalDiscountAmount = useMemo(
      () =>
          totalPrice.productDiscountAmountShown +
          totalPrice.couponDiscountAmountShown +
          totalPrice.affiliateCodeDiscountAmountShown +
          totalPrice.couponCodeDiscountAmountShown,
      [
        totalPrice.productDiscountAmountShown,
        totalPrice.couponDiscountAmountShown,
        totalPrice.affiliateCodeDiscountAmountShown,
        totalPrice.couponCodeDiscountAmountShown
      ]
  );

  useEffect(() => {
    setCodeDiscountAmount(
        Math.max(
            totalPrice.affiliateCodeDiscountAmount, totalPrice.couponCodeDiscountAmount
        ));
    // eslint-disable-next-line
  }, [totalDiscountAmount]);

  return (
      <>
        <MainWrapper showFooter={true} mainClassName={style.orderAndPayment}>
          <>
            <div className={style.title}>
              <ArticleTitle title="주문/결제" className={style.textArea} />
            </div>
            <OrderProductsField />

            <div className={style.orderAndPaymentWrapper}>
              {/*  쿠폰 할인  */}
              <CouponDiscountField totalPrice={totalPrice} selectedCouponIdx={selectedCouponIdx}
                                   resetSalesCodeDiscount={resetSalesCodeDiscount} setSelectedCouponIdx={setSelectedCouponIdx}
                                   setCouponDiscountAmount={setCouponDiscountAmount} />

              {currentVoucherType === UnitVoucherType.COUNSELOR ? (
                  isMobile ? (
                        <>
                          {/*  결제 수단 */}
                          <PaymentMethodField paymentWay={paymentWay} onClickPaymentWayBtn={onClickPaymentWayBtn} />
                          {/*  주문 정보  */}
                          <OrderSummaryField totalPrice={totalPrice} totalDiscountAmount={totalDiscountAmount}
                                             selectedCouponIdx={selectedCouponIdx} codeDiscountAmount={codeDiscountAmount} />
                        </>
                    ) : (
                        <>
                          {/*  결제 수단 */}
                          <OrderSummaryField totalPrice={totalPrice} totalDiscountAmount={totalDiscountAmount} isGirdField={true}
                                             selectedCouponIdx={selectedCouponIdx} codeDiscountAmount={codeDiscountAmount} />
                          {/*  주문 정보  */}
                          <PaymentMethodField paymentWay={paymentWay} onClickPaymentWayBtn={onClickPaymentWayBtn} />
                        </>
                    )
              ) : (
                  <>
                    {isMobile ? (
                        <>
                          {/*코드 할인*/}
                          <CodeDiscountPanel
                              discountCode={enteredCode}
                              setDiscountCode={setEnteredCode}
                              setCode={resetAndSetCode}
                              cartIdxes={cartIdxes}
                              cartedProducts={cartedProductList ?? []}
                          />
                          {/*  결제 수단 */}
                          <PaymentMethodField paymentWay={paymentWay} onClickPaymentWayBtn={onClickPaymentWayBtn} />
                        </>
                    ): (
                        <>
                          {/*  결제 수단 */}
                          <PaymentMethodField paymentWay={paymentWay} onClickPaymentWayBtn={onClickPaymentWayBtn} />
                          {/*코드 할인*/}
                          <CodeDiscountPanel
                              discountCode={enteredCode}
                              setDiscountCode={setEnteredCode}
                              setCode={resetAndSetCode}
                              cartIdxes={cartIdxes}
                              cartedProducts={cartedProductList ?? []}
                          />
                        </>
                    )}

                    {/*  주문 정보  */}
                    <OrderSummaryField totalPrice={totalPrice} totalDiscountAmount={totalDiscountAmount}
                                       selectedCouponIdx={selectedCouponIdx} codeDiscountAmount={codeDiscountAmount} />
                  </>
                )}
            </div>

            {/*  약관 동의  */}
            <AgreementField isAgreed={isAgreed} onClickAgreeBtn={onClickAgreeBtn} onClickTermsLink={onClickTermsLink} />
            <button className={style.payButton} onClick={onClickPayBtn}>결제하기</button>
          </>
        </MainWrapper>
        {
          <PaymentGateway
              isClickedPayButton={isClickedPayButton}
              resetClickedPayButton={() => setIsClickedPayButton(false)}
              paymentWay={paymentWay}
              cartIdxes={cartIdxes}
              price={totalPrice}
              couponHistoryIdx={selectedCouponIdx}
              affiliateCodeIdx={affiliateCode?.idx ?? null}
              couponCodeIdx={couponCode?.couponCodeIdx ?? null}
          />
        }
      </>
  );
}
